export const resolveSelectedTemplateLabel = (
  selectedTemplateIds: string[],
  templateLabelById: Record<string, string>,
): string => {
  if (!selectedTemplateIds.length) return "Standardvorlage"

  if (selectedTemplateIds.length > 1)
    return `${selectedTemplateIds.length} unterschiedliche Vorlagen`

  return templateLabelById[selectedTemplateIds[0]] ?? selectedTemplateIds[0]
}

type ContactOrderLike = {
  steps?: Array<{ channel?: string | null }> | null
}

type BillingPlanStateLike = {
  plan?: {
    pricing?: Array<{ metric?: string | null; price_per_unit_cents?: number | null }> | null
  } | null
} | null

type ExpectedConsumptionSummaryInput = {
  createdOrdersCount: number
  mobileMessageCount: number
  billingPlanState: BillingPlanStateLike
  numberFormatter: Intl.NumberFormat
}

export type ExpectedConsumptionSummary = {
  valueLabel: string
  subLabel: string
  tooltipContent: string
  facts: Array<{ label: string; value: string }>
  calculable: boolean
}

const WORK_ORDER_COMPLETION_METRIC = "work_order_completion"
const MOBILE_MESSAGE_DELIVERY_METRIC = "mobile_message_delivery"

const formatCredits = (credits: number, numberFormatter: Intl.NumberFormat) =>
  `${numberFormatter.format(credits)} ${credits === 1 ? "Credit" : "Credits"}`

const pricingCreditsForMetric = (
  billingPlanState: BillingPlanStateLike,
  metric: string,
): number | null => {
  const price = billingPlanState?.plan?.pricing?.find(
    (entry) => entry.metric === metric,
  )?.price_per_unit_cents

  return typeof price === "number" && Number.isFinite(price) ? price : null
}

export const countExpectedMobileMessageSteps = (contactOrders: ContactOrderLike[]) =>
  contactOrders.reduce(
    (total, order) =>
      total + (order.steps ?? []).filter((step) => step.channel === "MOBILE_MESSAGE").length,
    0,
  )

export const buildExpectedConsumptionSummary = ({
  createdOrdersCount,
  mobileMessageCount,
  billingPlanState,
  numberFormatter,
}: ExpectedConsumptionSummaryInput): ExpectedConsumptionSummary => {
  const workOrderCreditsPerUnit = pricingCreditsForMetric(
    billingPlanState,
    WORK_ORDER_COMPLETION_METRIC,
  )
  const mobileCreditsPerUnit = pricingCreditsForMetric(
    billingPlanState,
    MOBILE_MESSAGE_DELIVERY_METRIC,
  )
  const canCalculate =
    workOrderCreditsPerUnit !== null && (mobileMessageCount === 0 || mobileCreditsPerUnit !== null)

  if (!canCalculate) {
    return {
      valueLabel: "Nicht berechenbar",
      subLabel: "voraussichtlich bei Ausführung/Versand",
      tooltipContent:
        "Der aktive Tarif konnte nicht geladen werden. Briefe sind in dieser Erwartungsrechnung nicht enthalten.",
      facts: [
        { label: "Aufträge", value: numberFormatter.format(createdOrdersCount) },
        { label: "Mobile Nachrichten", value: numberFormatter.format(mobileMessageCount) },
      ],
      calculable: false,
    }
  }

  const workOrderCredits = createdOrdersCount * workOrderCreditsPerUnit
  const mobileMessageCredits = mobileMessageCount * (mobileCreditsPerUnit ?? 0)
  const totalCredits = workOrderCredits + mobileMessageCredits

  return {
    valueLabel: formatCredits(totalCredits, numberFormatter),
    subLabel: "voraussichtlich bei Ausführung/Versand",
    tooltipContent:
      "Erwarteter Verbrauch aus Auftragsabschluss und Mobile-Message-Versand. Briefe sind hier bewusst nicht enthalten.",
    facts: [
      {
        label: "Aufträge",
        value: `${numberFormatter.format(createdOrdersCount)} / ${formatCredits(
          workOrderCredits,
          numberFormatter,
        )}`,
      },
      {
        label: "Mobile Nachrichten",
        value: `${numberFormatter.format(mobileMessageCount)} / ${formatCredits(
          mobileMessageCredits,
          numberFormatter,
        )}`,
      },
    ],
    calculable: true,
  }
}

export const buildBatchSummaryNextSteps = () => [
  "Terminbrief-To-dos prüfen, sobald die digitalen Kontaktwege abgelaufen sind.",
]
